import React, { useEffect, useRef, useState } from "react";
import styles from './Instructors.module.scss';
import PageTitle from "../../../components/pageTitle/PageTitle";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import LoadMore from "../../../components/loadMore/loadMore";
import StudentAllocationCard from "../../../components/studentAllocationCard/StudentAllocationCard";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/modal/Modal";
import UploadForm from "../../../components/uploadForm/UploadForm";
import Filter from "../../../components/filter/Filter";

const Instructors = () => {
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('Permanent Instructors');
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(100);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const progressRef = useRef(0);
  const instructorTemplateUrl="https://maestrostoragedev.blob.core.windows.net/maestro/files/template_for_instructor_upload.xlsx"    
    
  useEffect(() => {
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      if (selectedFilter === 'Permanent Instructors') {
        await axiosClient.get(`users/instructor-management/instructos`, {
          params: {
            limit: limit,
            page: page,
          }
        })
        .then ((response) => {
          if(response.data?.instructors) {
            if(page === 1) {
              setInstructors([]);
            }
            setInstructors((prevData) => {
              return [...prevData, ...response.data.instructors.map((instructor) => ({
                id: instructor.id,
                profilePicture: instructor?.profilePicture ?? null,
                firstName: instructor?.firstName ?? '',
                lastName: instructor?.lastName ?? '',
                tag: { text: instructor.isActive ? 'Active' : 'Inactive', variant: instructor.isActive ? 5 : 6 },
                data: [
                  {
                    label: 'Clinical Site',
                    value: instructor?.clinicalSites.length > 0 ? 
                    `${instructor?.clinicalSites[0].name} ${instructor?.clinicalSites.length > 1 
                      ? `+${instructor?.clinicalSites.length - 1}` : ''}` : 
                    '',
                  tooltip: instructor?.clinicalSites.length > 1 ? 
                    instructor?.clinicalSites.map(item => item.name).join(', ') :
                    ''
                  },
                  {
                    label: 'Health System',        
                    value: instructor?.healthSystems.length > 0 ? 
                      `${instructor?.healthSystems[0].name} ${instructor?.healthSystems.length > 1 
                        ? `+${instructor?.healthSystems.length - 1}` : ''}` : 
                      '',
                    tooltip: instructor?.healthSystems.length > 1 ? 
                      instructor?.healthSystems.map(item => item.name).join(', ') :
                      ''
                  },
                  {
                    label: 'Speciality',
                    value: instructor?.specialities.length > 0 ? 
                    `${instructor?.specialities[0].name} ${instructor?.specialities.length > 1 
                      ? `+${instructor?.specialities.length - 1}` : ''}` : 
                    '',
                  tooltip: instructor?.specialities.length > 1 ? 
                    instructor?.specialities.map(item => item.name).join(', ') :
                    ''
                  },
                  {
                    label: 'Students',
                    value: instructor?.allocatedStudentCount ?? ''
                  },
                  {
                    label: 'Email',
                    value: instructor?.email ?? ''
                  },
                  {
                    label: 'Phone',
                    value: instructor?.phone ?? ''
                  },
                ],
              }))];
            });
            setTotalPages(response.data.totalPages);
          }
        })
        .catch((error) => {
          showToast(error.message, 'error');
        })
        .finally(() => {
          setIsLoading(false);
          setIsLoadMoreLoading(false);
        });
      } else {
        await axiosClient.get(`users/temporary-instructors`, {
          params: {
            limit: limit,
            page: page,
          }
        })
        .then ((response) => {
          if(response.data?.instructors) {
            if(page === 1) {
              setInstructors([]);
            }
            setInstructors((prevData) => {
              return [...prevData, ...response.data.instructors.map((instructor) => ({
                id: instructor.id,
                profilePicture: instructor?.profilePicture ?? null,
                firstName: instructor?.firstName ?? '',
                lastName: instructor?.lastName ?? '',
                data: [
                  {
                    label: 'Clinical Site',
                    value: '-'
                  },
                  {
                    label: 'Health System',        
                    value: '-'
                  },
                  {
                    label: 'Speciality',
                    value: '-'
                  },
                  {
                    label: 'Email',
                    value: instructor?.email ?? ''
                  },
                  {
                    label: 'Phone',
                    value: instructor?.phone ?? '-'
                  },
                ],
              }))];
            });
            setTotalPages(response.data.totalPages);
          }
        })
        .catch((error) => {
          showToast(error.message, 'error');
        })
        .finally(() => {
          setIsLoading(false);
          setIsLoadMoreLoading(false);
        });
      }
      
    };

    fetchData();
  }, [page, showToast, selectedFilter, refetchTrigger]);

  const loadMore = async () => {
    setPage(page + 1);
  };

  const showProgress = () => {
    setUploadProgress(0);
    progressRef.current = 0;
    
    const interval = setInterval(() => {
      progressRef.current = progressRef.current + 1; 
      
      setUploadProgress(progressRef.current);
      
      if (progressRef.current >= 100) {
        clearInterval(interval);
      }
    }, 10);
  
    return () => clearInterval(interval);
  };  

  const handleUpload = async (file) => {
    const formData = new FormData();
    if(file) {
      formData.append('instructorInfoFile', file);
    }
    setIsUploadLoading(true);
    showProgress();
    await axiosClient.post(`instructors/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    })
    .then((response) => {
      showToast(response.message);
      setShowUploadModal(false);
      setRefetchTrigger(!refetchTrigger);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsUploadLoading(false);
    });
  };

  return (
    <>
     {isLoading && <LoadingOverlay/>}
      <div className={styles.header}>
        <PageTitle
          label="Instructor Management"
        />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={[
                {
                  label: 'Permanent Instructors',
                  value: 'Permanent Instructors',
                },
                {
                  label: 'Temporary Instructor',
                  value: 'Temporary Instructor',
                }
              ]}
              selected={selectedFilter}
              onChange={(filter) => {
                setSelectedFilter(filter);
              }}
            />
          </div>
          <div style={{flex:'none'}}>
            <DropDownButton
              label="Manage Instructors"
              items={[
                {
                  label: 'Add New Instructor   ',
                  onClick: () => navigate('/instructors/create')
                },
                {
                  label: 'Bulk Upload Instructors',
                  onClick: () => {setShowUploadModal(true)}
                },
                {
                  label: 'Availability',
                  onClick: () => navigate('/instructors/availability')
                }
              ]}
            />
          </div>
        </div>
        { instructors.length > 0 ? <div className={styles.cardList}>
          {instructors.map((instructor, index) => {
            return (
              <div className={styles.cardItem} key={`instructor-${index}`}>
                <StudentAllocationCard
                  {...instructor}
                  showActions={true}
                  actions={[
                    {
                      label: selectedFilter === 'Permanent Instructors' ? 'Edit' : 'Approve',
                      onClick: () => navigate(`/instructors/edit/${instructor.id}`)
                    }
                  ]}
                  onClick={() => navigate(`/instructors/edit/${instructor.id}`)}
                />
              </div>
            );
          })}
          {(isLoadMoreLoading || page < totalPages) 
            && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.emptyInstructors}>
          <p>You have no instructors at this time.</p>
        </div>}
      </div>
      {showUploadModal && <Modal>
        <UploadForm
          isUploading={isUploadLoading}
          uploadProgress={uploadProgress}
          onClose={() => setShowUploadModal(false)}
          onSubmit={handleUpload}
          buttonLabel="Import Instructors"
          templateFileUrl={instructorTemplateUrl}
        />
      </Modal>}
    </>
  );
};

export default Instructors;